import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

export type ThemePreference = "light" | "dark" | "system";
export type ThemeMode = "light" | "dark";

export interface ThemeContextValue {
	mode: ThemeMode;
	preference: ThemePreference;
	setPreference: (next: ThemePreference) => void;
	setOverrideMode: (mode: ThemeMode | null) => void;
}

export const STORAGE_KEY = "ekedcThemePreference";

const isThemePreference = (v: string | null): v is ThemePreference =>
	v === "light" || v === "dark" || v === "system";

const readStoredPreference = (): ThemePreference => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (isThemePreference(stored)) return stored;
	} catch {
		/* localStorage unavailable */
	}
	return "system";
};

const systemPrefersDark = (): boolean => {
	if (typeof window === "undefined" || !window.matchMedia) return false;
	return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const resolveMode = (preference: ThemePreference): ThemeMode => {
	if (preference === "light" || preference === "dark") return preference;
	return systemPrefersDark() ? "dark" : "light";
};

const applyDocumentTheme = (mode: ThemeMode) => {
	if (typeof document === "undefined") return;
	document.documentElement.setAttribute("data-theme", mode);
};

export const ThemeContext = createContext<ThemeContextValue>({
	mode: "light",
	preference: "system",
	setPreference: () => undefined,
	setOverrideMode: () => undefined,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
	const [mode, setMode] = useState<ThemeMode>(() => resolveMode(readStoredPreference()));
	const [overrideMode, setOverrideModeState] = useState<ThemeMode | null>(null);

	useEffect(() => {
		const resolved = overrideMode ?? resolveMode(preference);
		setMode(resolved);
		applyDocumentTheme(resolved);
	}, [preference, overrideMode]);

	useEffect(() => {
		if (preference !== "system") return;
		if (typeof window === "undefined" || !window.matchMedia) return;
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = (e: MediaQueryListEvent) => {
			if (overrideMode !== null) return;
			const next: ThemeMode = e.matches ? "dark" : "light";
			setMode(next);
			applyDocumentTheme(next);
		};
		if (mq.addEventListener) mq.addEventListener("change", onChange);
		else mq.addListener(onChange);
		return () => {
			if (mq.removeEventListener) mq.removeEventListener("change", onChange);
			else mq.removeListener(onChange);
		};
	}, [preference, overrideMode]);

	const setPreference = useCallback((next: ThemePreference) => {
		setPreferenceState(next);
		try {
			if (next === "system") localStorage.removeItem(STORAGE_KEY);
			else localStorage.setItem(STORAGE_KEY, next);
		} catch {
			/* localStorage unavailable */
		}
	}, []);

	const setOverrideMode = useCallback((next: ThemeMode | null) => {
		setOverrideModeState(next);
	}, []);

	const value = useMemo<ThemeContextValue>(
		() => ({ mode, preference, setPreference, setOverrideMode }),
		[mode, preference, setPreference, setOverrideMode],
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
